import React, { useState, useEffect } from 'react';
import { callApi } from '../api';
import {
    Shield, Search, Filter, RefreshCw,
    Trash2, Edit, Plus, User, LogIn, AlertTriangle
} from 'lucide-react';
import './AuditLog.css';

function AuditLogs() {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isLive, setIsLive] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterAction, setFilterAction] = useState('ALL');

    const fetchLogs = async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const data = await callApi('/admin/audit-logs', 'GET');
            // Assuming data.logs is the array
            // If the API returns different structure, we fallback
            const logArray = data.logs || data || [];

            // Sort by Date Descending
            const sorted = [...logArray].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

            setLogs(sorted);
        } catch (e) {
            console.error(e);
        } finally {
            if (!silent) setLoading(false);
        }
    };

    useEffect(() => {
        fetchLogs();
    }, []);

    // LIVE WATCH EFFECT
    useEffect(() => {
        let interval;
        if (isLive) {
            interval = setInterval(() => {
                fetchLogs(true); // Silent update
            }, 3000); // 3 seconds
        }
        return () => clearInterval(interval);
    }, [isLive]);

    // FILTER LOGIC
    const filteredLogs = logs.filter(log => {
        const matchesSearch =
            (log.userCode || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (log.details || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (log.ip || '').includes(searchTerm);

        const matchesFilter = filterAction === 'ALL' || getActionCategory(log.action, log.details) === filterAction;

        return matchesSearch && matchesFilter;
    });

    // Helper to categorize actions for color/icon
    function formatUserAgent(ua) {
        if (!ua || ua === "Bilinmiyor" || ua === "unknown") return "Bilinmiyor";
        let os = "Masaüstü";
        let browser = "Web";
        
        if (ua.includes('Windows NT 10.0')) os = "Windows 10/11";
        else if (ua.includes('Windows NT')) os = "Windows";
        else if (ua.includes('Mac OS X')) os = "macOS";
        else if (ua.includes('iPhone')) os = "iPhone";
        else if (ua.includes('iPad')) os = "iPad";
        else if (ua.includes('Android')) os = "Android";
        
        if (ua.includes('Edg/')) browser = "Edge";
        else if (ua.includes('Chrome/')) browser = "Chrome";
        else if (ua.includes('Firefox/')) browser = "Firefox";
        else if (ua.includes('Safari/') && !ua.includes('Chrome')) browser = "Safari";
        else if (ua.includes('Opera/') || ua.includes('OPR/')) browser = "Opera";
        
        return `${os} - ${browser}`;
    }

    function getActionCategory(action, details) {
        const a = (action || '').toLowerCase();
        const d = (details || '').toLowerCase();
        
        if (d.includes('giriş') || a.includes('login') || a === 'get') return 'LOGIN';
        if (a === 'post' || d.includes('eklendi')) return 'CREATE';
        if (a === 'delete' || d.includes('silindi')) return 'DELETE';
        if (a === 'put' || d.includes('güncellendi') || d.includes('sıfırlandı')) return 'UPDATE';
        return 'OTHER';
    }

    // Helper for Icon
    function getActionIcon(category) {
        switch (category) {
            case 'LOGIN': return <LogIn size={14} />;
            case 'CREATE': return <Plus size={14} />;
            case 'DELETE': return <Trash2 size={14} />;
            case 'UPDATE': return <Edit size={14} />;
            default: return <Shield size={14} />;
        }
    }

    return (
        <div className="audit-container">
            <div className="audit-header">
                <h1>
                    <Shield size={32} color="#2563eb" />
                    Denetim Kayıtları
                </h1>

                <button
                    className={`live-toggle ${isLive ? 'active' : 'inactive'}`}
                    onClick={() => setIsLive(!isLive)}
                >
                    <div className="pulse-dot"></div>
                    {isLive ? 'Canlı İzleniyor' : 'Canlı İzle'}
                </button>
            </div>

            <div className="audit-toolbar">
                <div style={{ position: 'relative', flex: 1 }}>
                    <Search size={18} color="#9ca3af" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
                    <input
                        type="text"
                        placeholder="Kullanıcı, IP veya İşlem ara..."
                        className="search-input"
                        style={{ paddingLeft: '2.5rem' }}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Filter size={18} color="#6b7280" />
                    <select
                        className="filter-select"
                        value={filterAction}
                        onChange={(e) => setFilterAction(e.target.value)}
                    >
                        <option value="ALL">Tüm İşlemler</option>
                        <option value="LOGIN">Girişler (Login)</option>
                        <option value="CREATE">Ekleme (Create)</option>
                        <option value="UPDATE">Düzenleme (Update)</option>
                        <option value="DELETE">Silme (Delete)</option>
                    </select>
                </div>

                <button
                    onClick={() => fetchLogs()}
                    title="Yenile"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px' }}
                >
                    <RefreshCw size={20} color="#6b7280" />
                </button>
            </div>

            <div className="audit-table-wrapper">
                <table className="audit-table">
                    <thead>
                        <tr>
                            <th>Zaman</th>
                            <th>Kullanıcı</th>
                            <th>IP Adresi</th>
                            <th>Cihaz</th>
                            <th>İşlem Türü</th>
                            <th>Detaylar</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && logs.length === 0 ? (
                            <tr><td colSpan="6" style={{ textAlign: 'center', padding: '2rem' }}>Yükleniyor...</td></tr>
                        ) : filteredLogs.length > 0 ? (
                            filteredLogs.map(log => {
                                const category = getActionCategory(log.action, log.details);
                                return (
                                    <tr key={log.id}>
                                        <td style={{ color: '#6b7280', fontSize: '0.85rem' }}>
                                            {new Date(log.createdAt).toLocaleString('tr-TR')}
                                        </td>
                                        <td>
                                            <div className="user-cell">
                                                <div className="user-avatar">
                                                    <User size={16} />
                                                </div>
                                                {log.userCode || 'Sistem'}
                                            </div>
                                        </td>
                                        <td>
                                            <span className="ip-badge">{log.ip}</span>
                                        </td>
                                        <td>
                                            <span className={`action-badge ${category.toLowerCase()}`}>
                                                {getActionIcon(category)}
                                                {log.action}
                                            </span>
                                        </td>
                                        <td style={{ color: '#4b5563' }}>
                                            {log.details || '-'}
                                        </td>
                                    </tr>
                                )
                            })
                        ) : (
                            <tr><td colSpan="6" style={{ textAlign: 'center', padding: '2rem', color: '#9ca3af' }}>Kayıt bulunamadı.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default AuditLogs;
